/* Rotating announcement bar.
   Autoplay pauses on hover, on keyboard focus, while the tab is hidden, and
   when the visitor prefers reduced motion. */

(function () {
  'use strict';

  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  const DEFAULT_INTERVAL = 3500;

  class AyniAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.track || this.slides.length === 0) return;

      this.index = 0;
      this.timer = null;
      this.editorLocked = false;
      this.interval = Number(this.dataset.interval) || DEFAULT_INTERVAL;
      this.motionQuery = window.matchMedia(REDUCED_MOTION);

      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);

      this.render();

      if (this.slides.length > 1) {
        this.addListeners();
        this.resume();
      }
    }

    disconnectedCallback() {
      this.pause();
      this.removeListeners();
    }

    addListeners() {
      document.addEventListener('visibilitychange', this.handleVisibility);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      this.motionQuery.addEventListener('change', this.handleMotionChange);
      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);
    }

    removeListeners() {
      document.removeEventListener('visibilitychange', this.handleVisibility);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      this.motionQuery.removeEventListener('change', this.handleMotionChange);
      this.removeEventListener('mouseenter', this.pause);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('focusin', this.pause);
      this.removeEventListener('focusout', this.resume);
    }

    render() {
      this.track.style.transform = 'translateX(-' + this.index * 100 + '%)';

      this.slides.forEach((slide, i) => {
        const isActive = i === this.index;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach((el) => {
          el.tabIndex = isActive ? 0 : -1;
        });
      });
    }

    goTo(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.render();
    }

    next() {
      this.goTo(this.index + 1);
    }

    resume() {
      if (this.timer) return;
      if (this.editorLocked) return;
      if (this.slides.length < 2) return;
      if (this.motionQuery.matches) return;
      if (document.hidden) return;

      this.timer = window.setInterval(() => this.next(), this.interval);
    }

    pause() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    handleVisibility() {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    }

    handleMotionChange() {
      if (this.motionQuery.matches) {
        this.pause();
      } else {
        this.resume();
      }
    }

    handleBlockSelect(event) {
      const index = this.slides.findIndex(
        (slide) => slide.dataset.blockId === event.detail.blockId
      );
      if (index === -1) return;

      this.editorLocked = true;
      this.pause();
      this.goTo(index);
    }

    handleBlockDeselect(event) {
      const belongsHere = this.slides.some(
        (slide) => slide.dataset.blockId === event.detail.blockId
      );
      if (!belongsHere) return;

      this.editorLocked = false;
      this.resume();
    }
  }

  if (!customElements.get('ayni-announcement-bar')) {
    customElements.define('ayni-announcement-bar', AyniAnnouncementBar);
  }
})();